// Vendor Libs
import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import {map, now, orderBy, trim} from 'lodash';

// Core Libs
import downloadExcelFile from '../../lib/core/export';

// Components
import Button from '../core/button';
import Error from '../core/error';
import Icon from '../core/icon';
import Loading from '../core/loading';
import TableWithSort from '../core/table-with-sort';

// Styles
import './mass-action-response.scss';

function MassActionResponse({
    columnConfig,
    formData: {fetching, formErrors, location, response},
    label,
    onReset,
    username,
}) {
    const renderStatusPhrase = (dataRow) => {
        const {error, statusPhrase} = dataRow;

        return error ? <div className="mass-action-response--status-error">{statusPhrase}</div> : statusPhrase;
    };

    const getResponseColumnConfig = () =>
        map(columnConfig, (column) =>
            column.dataKey === 'statusPhrase' ? {...column, renderRowCallback: renderStatusPhrase} : column
        );

    const handleExportClick = (event) => {
        event && event.preventDefault && event.preventDefault();

        const responseData = orderBy(response, ['error', 'statusPhrase', 'description'], ['desc', 'asc', 'asc']);

        if (responseData.length) {
            try {
                downloadExcelFile(responseData, {}, `Mass Actions Result-${now()}.xlsx`, username);
            } catch (error) {
                alert('Export failed. Please try again or contact support if the issue persists.');
            }
        } else {
            alert('No data available to export.');
        }
    };

    const renderResponseMessage = () => {
        const correlationId = trim(location, '/');
        const responseData = orderBy(response, ['error', 'statusPhrase', 'description'], ['desc', 'asc', 'asc']);

        return responseData.length ? (
            <>
                <TableWithSort
                    className="mass-action-response--table-with-sort"
                    columnConfig={getResponseColumnConfig()}
                    localSort={true}
                    notSortable={[]}
                    searchData={responseData}
                />
                <div className="mass-action-response--table-with-sort--buttons">
                    {correlationId ? (
                        <Link
                            className="mass-action-response--button button"
                            to={`/investigation/mass-action-results/${correlationId}`}
                        >
                            View Results Page
                        </Link>
                    ) : null}
                    <Button className="mass-action-response--button" onClick={handleExportClick}>
                        Export XLSX
                    </Button>
                    <Button className="mass-action-response--button" onClick={onReset}>
                        OK
                    </Button>
                </div>
            </>
        ) : correlationId ? (
            <div className="mass-action-response--table-with-sort--buttons">
                <Button className="mass-action-response--button" onClick={onReset}>
                    Continue
                </Button>
                <Link
                    className="mass-action-response--button button"
                    to={`/investigation/mass-action-results/${correlationId}`}
                >
                    View Results Page
                </Link>
            </div>
        ) : null;
    };

    return (
        <div className="mass-action-response">
            <div className="mass-action-response--overlay-wrapper">
                <div className="mass-action-response--overlay">
                    <Button className="mass-action-response--close" type="link" onClick={onReset}>
                        <Icon fontType="fa" iconName="close" />
                    </Button>
                    <div className="mass-action-response--title">Mass Actions Response</div>
                    <div className="mass-action-response--label">{label}</div>
                    <div className="mass-action-response--loading">
                        {fetching ? <div className="mass-action-response--loading-text">Loading Results...</div> : null}
                        <Loading loading={fetching} />
                    </div>
                    <Error error={formErrors} onReset={onReset} />
                    {renderResponseMessage()}
                </div>
            </div>
        </div>
    );
}

MassActionResponse.propTypes = {
    columnConfig: PropTypes.arrayOf(PropTypes.object).isRequired,
    formData: PropTypes.object,
    label: PropTypes.string,
    onReset: PropTypes.func,
    username: PropTypes.string,
};

export default MassActionResponse;
